import { Box, Flex, Stack, Text, useDisclosure } from '@chakra-ui/react'
import { faMagnifyingGlass } from '@fortawesome/pro-duotone-svg-icons'

import isTypedArrayMember from '@/common/utils/typescript/isTypedArrayMember'
import type { RestrictedPagePotentiallySuspendedDataFragment } from '@/server/data/generated/graphql'

import { PrimaryTrainingType, SECONDARY_TRAINING_TYPES } from '@/queries/constants'
import {
  DETECTED_ISSUE_TYPE_TO_SUSPENSION_EXPLANATION,
  DETECTED_ISSUE_TYPES,
  DETECTED_ISSUE_TYPES_WITH_SUSPENSION_EXPLANATION,
  type DetectedIssueType,
} from '@/client/services/moderation'
import shouldShowSuspensionNote from '@/client/services/moderation/shouldShowSuspensionNote'
import * as url from '@/client/services/url'
import { getScheduleUrl } from '@/client/services/url'
import {
  SUSPENSION_ACTION_CONTEXT_TO_EXPLANATION,
  type SuspensionAction,
  SuspensionActionContext,
} from '@/client/services/user'

import ShwLink from '@/components/ShwLink'
import TrainingCard from '@/components/Training/TrainingCard'
import TrainingModal from '@/components/Training/TrainingModal'
import type { TrainingUI } from '@/components/Training/types'
import RichText from '@/components/UI/RichTextEditor/RichText'

import RestrictedPageAccess from './RestrictedPageAccess'

function dataToDetectedIssueType(
  data?: RestrictedPagePotentiallySuspendedDataFragment
): DetectedIssueType | null {
  const detectedIssueType = data?.platform_feedback?.detected_issue_type
  if (detectedIssueType == null || !isTypedArrayMember(detectedIssueType, DETECTED_ISSUE_TYPES)) {
    return null
  }
  return detectedIssueType
}

function dataToTrainingUI(
  data?: RestrictedPagePotentiallySuspendedDataFragment
): TrainingUI | null {
  if (data == null || data.trainings == null || data.trainings.length === 0) {
    return null
  }
  const training = data.trainings[0]

  const learnerAttendanceTraining: TrainingUI = {
    id: training.id,
    slug: training.slug,
    title: training.title,
    excerpt: training.excerpt,
    description: training.description,
    tagline: training.tagline ?? null,
    primaryType: PrimaryTrainingType[training.primary_type],
    secondaryType: SECONDARY_TRAINING_TYPES[training.secondary_type],
    timeEstimateMinutes: training.time_estimate_minutes,
    link: training.link_training,
    linkSummary: training.link_summary,
    createdAt: training.created_at,
    submission: null,
  }
  return learnerAttendanceTraining
}

export default function RestrictedPagePotentiallySuspendedApp({
  data,
  suspensionAction,
  detectedIssueType,
  learnerAttendanceTraining,
}: {
  data?: RestrictedPagePotentiallySuspendedDataFragment
  suspensionAction: SuspensionAction
  detectedIssueType?: DetectedIssueType | null
  learnerAttendanceTraining?: TrainingUI | null
}): JSX.Element {
  // TODO: revisit the copy of this screen with Mariah. Made a row on the moderation system tracker.
  const { context, note } = suspensionAction
  // This suspension type is new and may lead to a lot of suspensions, so adding some custom logic to make the suspension screen less harsh and have users reach out to Intercom
  const learnerNoShow = suspensionAction?.context === SuspensionActionContext.LearnerNoShows
  const registrationRemoved =
    suspensionAction?.context !== SuspensionActionContext.SessionRemoval &&
    suspensionAction?.context !== SuspensionActionContext.OnboardingRejection
  const { isOpen: isModalOpen, onClose: onModalClose, onToggle: onModalToggle } = useDisclosure()

  // N.B. For both learnerAttendanceTrainingUI and issueType, we follow a highly unusual pattern.
  // 1. First, we try to derive their value from properties bearing information passed down from QueryBuilder.
  // 2. Second, if the information from QueryBuilder is unavailable, we try to derive their value from the
  //    data property, which bears data passed down from a page-level GraphQL query.
  //
  // We use (1) when this component is rendered with Pages Router. We use (2) when this component is rendered
  // with App Router because then QueryBuilder is unavailable.

  const learnerAttendanceTrainingUI = learnerAttendanceTraining ?? dataToTrainingUI(data) ?? null

  const issueType = detectedIssueType ?? dataToDetectedIssueType(data) ?? null
  const automaticSuspensionExplanation = isTypedArrayMember(
    issueType,
    DETECTED_ISSUE_TYPES_WITH_SUSPENSION_EXPLANATION
  )
    ? DETECTED_ISSUE_TYPE_TO_SUSPENSION_EXPLANATION[issueType]
    : null

  return (
    <RestrictedPageAccess
      alignCenter
      faIcon={faMagnifyingGlass}
      iconColor="primary.fg"
      title={
        !learnerNoShow ? 'Your account is under review' : 'Your account is temporarily suspended'
      }
      hideHomeButton
      hideLoginButton
      description={
        <Stack direction="column" spacing="20px">
          {isModalOpen && learnerAttendanceTrainingUI != null ? (
            <TrainingModal training={learnerAttendanceTrainingUI} onClose={onModalClose} />
          ) : null}
          {shouldShowSuspensionNote(suspensionAction) ? (
            <RichText content={note} />
          ) : automaticSuspensionExplanation != null ? (
            <>
              <Text>Hi there,</Text>
              <RichText content={automaticSuspensionExplanation} />
              <Text>
                Please also remember that attempting to create or use another account during this
                time is not allowed and will result in a permanent ban from Schoolhouse.
              </Text>
              <Text>Thank you for your understanding.</Text>
              <Text color="fg.muted">
                If you believe this suspension was made in error, please contact{' '}
                <ShwLink href={url.faq.getArticleUrl('how-do-i-contact-the-schoolhouse-team')}>
                  User Support
                </ShwLink>{' '}
                for help.
              </Text>
            </>
          ) : (
            <>
              <Text>
                Your account has been{' '}
                {!learnerNoShow ? 'flagged for review ' : 'temporarily suspended '}
                for the following reason: <i>{SUSPENSION_ACTION_CONTEXT_TO_EXPLANATION[context]}</i>
              </Text>
              {!learnerNoShow || learnerAttendanceTrainingUI == null ? (
                <Text>
                  Please contact{' '}
                  <ShwLink href={url.faq.getArticleUrl('how-do-i-contact-the-schoolhouse-team')}>
                    User Support
                  </ShwLink>{' '}
                  if you think there has been a mistake.
                </Text>
              ) : (
                <>
                  {registrationRemoved ? (
                    <Text>
                      You have been removed from any registered series. To recover your account,
                      complete the training below. Then, you can re-register for your old series
                      from the &quot;past&quot; tab of the{' '}
                      <ShwLink href={getScheduleUrl({ past: true })}>schedule</ShwLink> page.
                    </Text>
                  ) : (
                    <Text>To recover your account, please complete the training below.</Text>
                  )}
                  <Flex justifyContent="center">
                    <Box width="390px">
                      <TrainingCard
                        training={learnerAttendanceTrainingUI}
                        onSelect={onModalToggle}
                      />
                    </Box>
                  </Flex>
                </>
              )}
            </>
          )}
        </Stack>
      }
    />
  )
}
